import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { MdClose, MdLock } from 'react-icons/md';
import { toast } from 'react-toastify';
import { createOrganizationRole, updateOrganizationRole } from '../../../../services/Http/Api';
import MainButton from '../../../../shared/layout/buttons/MainButton';
import StyledModal from '../../../../shared/layout/modals/StyledModal';
import ModalBody from '../../../../shared/layout/modals/ModalBody';

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
`;

const ModalContainer = styled.div`
    background: white;
    border-radius: 12px;
    width: 90%;
    max-width: 800px;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const ModalHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.25rem 1.5rem;
    border-bottom: 1px solid #e5e7eb;

    h2 {
        margin: 0;
        font-size: 1.25rem;
        font-weight: 600;
        color: #111827;
        display: flex;
        align-items: center;
        gap: 0.75rem;
    }
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 8px;
    color: #6b7280;
    transition: all 0.2s;

    &:hover {
        background: #f3f4f6;
        color: #111827;
    }
`;

const ModalContent = styled.div`
    padding: 1.5rem;
`;

const FormGroup = styled.div`
    margin-bottom: 1.25rem;

    label {
        display: block;
        font-size: 0.875rem;
        font-weight: 500;
        color: #374151;
        margin-bottom: 0.375rem;
    }

    input, textarea {
        width: 100%;
        padding: 0.625rem 0.75rem;
        border: 1px solid #d1d5db;
        border-radius: 8px;
        font-size: 0.875rem;
        transition: all 0.2s;

        &:focus {
            outline: none;
            border-color: #821692;
            box-shadow: 0 0 0 3px rgba(130, 22, 146, 0.1);
        }

        &:disabled {
            background: #f3f4f6;
            color: #6b7280;
        }
    }

    textarea {
        min-height: 80px;
        resize: vertical;
    }
`;

const PermissionSection = styled.div`
    margin-top: 1.5rem;

    h3 {
        font-size: 1rem;
        font-weight: 600;
        color: #111827;
        margin: 0 0 1rem 0;
    }
`;

const PermissionTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    overflow: hidden;

    th, td {
        padding: 0.75rem 1rem;
        text-align: center;
        border-bottom: 1px solid #e5e7eb;
    }

    th {
        background: #f9fafb;
        font-size: 0.75rem;
        font-weight: 600;
        color: #6b7280;
        text-transform: uppercase;
        letter-spacing: 0.05em;
    }

    th:first-child, td:first-child {
        text-align: left;
        font-weight: 500;
    }

    tr:last-child td {
        border-bottom: none;
    }

    tr:hover {
        background: #f9fafb;
    }
`;

const ModuleName = styled.span`
    text-transform: capitalize;
    color: #374151;
`;

const RadioButton = styled.label`
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
    opacity: ${props => props.$disabled ? 0.5 : 1};

    input {
        width: 18px;
        height: 18px;
        cursor: inherit;
        accent-color: #821692;
    }
`;

const ModalFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    padding: 1.25rem 1.5rem;
    border-top: 1px solid #e5e7eb;
    background: #f9fafb;
`;

const CancelButton = styled.button`
    padding: 0.625rem 1.25rem;
    border: 1px solid #d1d5db;
    background: white;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background: #f3f4f6;
    }
`;

const RoleModal = ({ isOpen, onClose, onSave, mode, role, options }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        display_name: '',
        description: '',
        permissions: {},
    });
    const [saving, setSaving] = useState(false);

    const modules = options?.modules || [
        'users', 'customers', 'venues', 'items', 'products',
        'warranties', 'campaigns', 'transactions', 'discounts',
        'integrations', 'settings', 'roles'
    ];

    const permissionLevels = options?.permission_levels || ['none', 'read', 'write', 'delete'];

    useEffect(() => {
        if (mode === 'edit' && role) {
            setFormData({
                display_name: role.display_name || '',
                description: role.description || '',
                permissions: role.permissions || {},
            });
        } else {
            // Initialize all permissions to 'none' for new roles
            const initialPermissions = {};
            modules.forEach(module => {
                initialPermissions[module] = 'none';
            });
            setFormData({
                display_name: '',
                description: '',
                permissions: initialPermissions,
            });
        }
    }, [mode, role, modules]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const handlePermissionChange = (module, level) => {
        if (mode === 'edit' && role?.is_system_default) {
            // System roles can still have their permissions edited
        }
        setFormData(prev => ({
            ...prev,
            permissions: {
                ...prev.permissions,
                [module]: level,
            },
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.display_name.trim()) {
            toast.error(t('Users.Roles.nameRequired'));
            return;
        }

        setSaving(true);
        try {
            if (mode === 'edit') {
                await updateOrganizationRole(role.id, formData);
                toast.success(t('Users.Roles.updateSuccess'));
            } else {
                await createOrganizationRole(formData);
                toast.success(t('Users.Roles.createSuccess'));
            }
            onSave();
        } catch (error) {
            console.error('Error saving role:', error);
            toast.error(
                mode === 'edit'
                    ? t('Users.Roles.updateError')
                    : t('Users.Roles.createError')
            );
        } finally {
            setSaving(false);
        }
    };

    const getModuleLabel = (module) => {
        const labels = {
            users: t('Users.Roles.modules.users'),
            customers: t('Users.Roles.modules.customers'),
            venues: t('Users.Roles.modules.venues'),
            items: t('Users.Roles.modules.items'),
            products: t('Users.Roles.modules.products'),
            warranties: t('Users.Roles.modules.warranties'),
            campaigns: t('Users.Roles.modules.campaigns'),
            transactions: t('Users.Roles.modules.transactions'),
            discounts: t('Users.Roles.modules.discounts'),
            integrations: t('Users.Roles.modules.integrations'),
            settings: t('Users.Roles.modules.settings'),
            roles: t('Users.Roles.modules.roles'),
        };
        return labels[module] || module;
    };

    const getPermissionLabel = (level) => {
        const labels = {
            none: t('Users.Roles.permissions.none'),
            read: t('Users.Roles.permissions.read'),
            write: t('Users.Roles.permissions.write'),
            delete: t('Users.Roles.permissions.delete'),
        };
        return labels[level] || level;
    };

    if (!isOpen) return null;

    const isSystemRole = mode === 'edit' && role?.is_system_default;

    return (
        <ModalOverlay onClick={onClose}>
            <ModalContainer onClick={e => e.stopPropagation()}>
                <form onSubmit={handleSubmit}>
                    <ModalHeader>
                        <h2>
                            <MdLock color="#821692" />
                            {mode === 'edit'
                                ? t('Users.Roles.editRole')
                                : t('Users.Roles.createRole')}
                        </h2>
                        <CloseButton type="button" onClick={onClose}>
                            <MdClose size={24} />
                        </CloseButton>
                    </ModalHeader>

                    <ModalContent>
                        <FormGroup>
                            <label>{t('Users.Roles.roleName')}</label>
                            <input
                                type="text"
                                name="display_name"
                                value={formData.display_name}
                                onChange={handleInputChange}
                                placeholder={t('Users.Roles.roleNamePlaceholder')}
                                disabled={isSystemRole}
                            />
                        </FormGroup>

                        <FormGroup>
                            <label>{t('Users.Roles.description')}</label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                placeholder={t('Users.Roles.descriptionPlaceholder')}
                                disabled={isSystemRole}
                            />
                        </FormGroup>

                        <PermissionSection>
                            <h3>{t('Users.Roles.permissionsTitle')}</h3>
                            <PermissionTable>
                                <thead>
                                    <tr>
                                        <th>{t('Users.Roles.module')}</th>
                                        {permissionLevels.map(level => (
                                            <th key={level}>{getPermissionLabel(level)}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {modules.map(module => (
                                        <tr key={module}>
                                            <td>
                                                <ModuleName>{getModuleLabel(module)}</ModuleName>
                                            </td>
                                            {permissionLevels.map(level => (
                                                <td key={level}>
                                                    <RadioButton>
                                                        <input
                                                            type="radio"
                                                            name={`permission_${module}`}
                                                            checked={formData.permissions[module] === level}
                                                            onChange={() => handlePermissionChange(module, level)}
                                                        />
                                                    </RadioButton>
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </PermissionTable>
                        </PermissionSection>
                    </ModalContent>

                    <ModalFooter>
                        <CancelButton type="button" onClick={onClose}>
                            {t('common.cancel')}
                        </CancelButton>
                        <MainButton type="submit" disabled={saving}>
                            {saving
                                ? t('common.saving')
                                : mode === 'edit'
                                    ? t('common.update')
                                    : t('common.create')}
                        </MainButton>
                    </ModalFooter>
                </form>
            </ModalContainer>
        </ModalOverlay>
    );
};

export default RoleModal;
